import { createApi } from '@reduxjs/toolkit/query/react';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice.ts';
import BaseQuery from './base.api';
import type { ExportedUser, UserFormMutation } from './models/admin.model.ts';
import type { Role } from './models/role.model.ts';
import type { User } from './models/user.model.ts';
import { usersApi } from './user.api.ts';

export const adminApi = createApi({
	reducerPath: 'adminApi',
	baseQuery: BaseQuery('/v1/admin'),
	tagTypes: ['Admin', 'User'],
	endpoints: (builder) => {
		return {
			getRoles: builder.query<
				{ internalRoles: Role[]; externalRoles: Role[]; apiRoles: Role[] },
				void
			>({
				query: () => ({
					url: `/roles`,
					method: 'GET',
					keepUnusedDataFor: 86400, // 24 hours
				}),
			}),
			getAccountsByNameAndLimit: builder.query<
				{ label: string; value: string }[],
				{ searchValue?: string; limit: number }
			>({
				query: ({ searchValue = '', limit }) => ({
					url: `/accounts-by-name-and-limit-by-user?searchValue=${encodeURIComponent(searchValue)}&limit=${limit}`,
					method: 'GET',
					responseHandler: async (response: Response) => {
						const data = await response.json();
						return Object.keys(data).map((entry) => {
							return { label: data[entry], value: entry };
						});
					},
				}),
				providesTags: ['Admin'],
			}),
			addUser: builder.mutation<void, { userData: UserFormMutation }>({
				query: ({ userData }) => ({
					url: `external-user`,
					method: 'POST',
					body: userData,
				}),
				invalidatesTags: ['Admin'],
			}),
			saveUser: builder.mutation<void, { userData: UserFormMutation }>({
				query: ({ userData }) => ({
					url: `external-user`,
					method: 'PUT',
					body: userData,
				}),
				invalidatesTags: ['Admin'],
			}),
			getAllUsersForExport: builder.query<ExportedUser[], void>({
				query: () => ({
					url: '/export-all-users',
					method: 'GET',
				}),
				async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
					dispatch(startLoading(requestId));
					try {
						await queryFulfilled;
					} finally {
						dispatch(stopLoading(requestId));
					}
				},
				keepUnusedDataFor: 5,
				providesTags: ['Admin'],
			}),
			getUserListForExport: builder.query<ExportedUser[], number, void>({
				query: (userId: number) => ({
					url: `/export-user-list?userId=${userId}`,
					method: 'GET',
				}),

				async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
					dispatch(startLoading(requestId));
					try {
						await queryFulfilled;
					} finally {
						dispatch(stopLoading(requestId));
					}
				},
				keepUnusedDataFor: 5,
				providesTags: ['Admin'],
			}),
			deactivateUser: builder.mutation<User, { userId: number }>({
				query: ({ userId }) => ({
					url: `/deactivate-user?userId=${userId}`,
					method: 'POST',
				}),
				// Keep this slice's own user-shaped cache fresh and ask the usersApi
				// slice to re-fetch the user list so the row flips to INACTIVE.
				invalidatesTags: ['Admin', 'User'],
				async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
					try {
						await queryFulfilled;
						dispatch(usersApi.util.invalidateTags(['User']));
					} catch {
						// Intentionally swallowed — call site handles user-facing
						// errors; catch only exists to prevent an unhandled-rejection
						// warning when the mutation fails.
					}
				},
			}),
			activateUser: builder.mutation<User, { userId: number }>({
				query: ({ userId }) => ({
					url: `/activate-user?userId=${userId}`,
					method: 'POST',
				}),
				invalidatesTags: ['Admin', 'User'],
				async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
					try {
						await queryFulfilled;
						dispatch(usersApi.util.invalidateTags(['User']));
					} catch {
						// Intentionally swallowed — call site handles user-facing
						// errors; catch only exists to prevent an unhandled-rejection
						// warning when the mutation fails.
					}
				},
			}),
		};
	},
});

export const {
	useGetRolesQuery,
	useGetAccountsByNameAndLimitQuery,
	useAddUserMutation,
	useSaveUserMutation,
	useLazyGetAllUsersForExportQuery,
	useLazyGetUserListForExportQuery,
	useDeactivateUserMutation,
	useActivateUserMutation,
} = adminApi;
